import { axios } from '@/plugins/http-axios';
import { defineStore } from 'pinia';
import { IOfficesOfRegion } from '@ifr/form-questionnaire';
import { IRole } from './user';

export interface ISysParameter {
  code: string;
  desc: string;
  value: string;
  resulttype: string;
}

interface ITakers {
  id: string;
  fio: string;
  roleName: string;
}

export interface IStatusBank {
  name: string;
  count: number;
  id: number;
}

interface ISigner {
  id: string;
  fio: string;
  roleName: string;
}

interface ICommonBank {
  id: number;
  name: string;
  inn: string;
  kpp: string;
}

export interface IBaseOffice {
  id: number;
  name: string;
  city: string;
  shortaddress: string;
  fulladdress: string;
}

export interface IBaseOffice2 extends IBaseOffice {
  cftcode: string;
  cftcalendar: string;
  bik: string;
  korr: string;
  korrlocation: string;
  branchcode: string;
  code: string;
  sortedItem: number;
  bankid: number;
  hide: boolean;
}

export interface IBankOffice extends IBaseOffice2 {
  regionid: number;
}

interface IBank2 extends ICommonBank {
  okpo: string;
  ogrn: string;
  regions: IBankRegion[];
}

export interface IBankRegion {
  id: number;
  name: string;
  hide: boolean;
  orderPosition: number;
  offices: IBankOffice[];
}

export interface IRegion {
  hide: boolean;
  id: number;
  name: string;
  orderPosition: number;
}

interface ISysSetting {
  code: string;
  desc: string;
  value: boolean;
  affectedSystems: string[];
}

interface IBankState {
  takers0: ITakers[];
  statuses: IStatusBank[];
  signers0: ISigner[];
  officesList0: IBankOffice[];
  regionsList0: IRegion[];
  sysParameters0: ISysParameter[];
  sysSettings0: ISysSetting[];
  bankingRegions0: IOfficesOfRegion[];
  roles0: IRole[];
}

export const useBankStore = defineStore('bank', {
  state: (): IBankState => ({
    takers0: [],
    statuses: [],
    signers0: [],
    officesList0: [],
    regionsList0: [],
    sysParameters0: [],
    sysSettings0: [],
    bankingRegions0: [],
    roles0: [],
  }),

  getters: {
    isBankingRegions: (state) => !!state.bankingRegions0.length,
    isRoles: (state) => !!state.roles0.length,
    roles: (state) => state.roles0,
    listRoles: (state) =>
      state.roles0.map((role) => ({
        text: role.name || 'Нет имени',
        value: role.id,
      })),
    bankingRegions: (state) => state.bankingRegions0,
    takers: (state) => state.takers0,
    statusesList: (state): IStatusBank[] => state.statuses,
    signers: (state) => state.signers0,
    officesList: (state) => state.officesList0,
    regionsList: (state) => state.regionsList0,
    sysParameters: (state) => state.sysParameters0,
    sysSettings: (state) => state.sysSettings0,
    sysSetting:
      (state) =>
      (key: string): boolean => {
        const o = state.sysSettings0.find((par: ISysSetting) => par.code === key);
        return o ? o.value : false;
      },
  },

  actions: {
    setSysSettings(data: ISysSetting[]) {
      this.sysSettings0 = data;
    },

    setSysSettingsValue({ code = '', value = false } = {}) {
      const sysPar = this.sysSettings.find((par) => par.code === code);
      if (sysPar) {
        sysPar.value = value;
      }
    },

    setSysParameters(data: ISysParameter[]) {
      this.sysParameters0 = data;
    },

    setTakers(data: ITakers[]) {
      this.takers0 = data;
    },

    setStatuses(statuses: IStatusBank[]) {
      this.statuses = statuses;
    },

    setSigners(data: ISigner[]) {
      this.signers0 = data;
    },

    setOffices(offices: IBankOffice[]) {
      this.officesList0 = offices;
    },

    setRegions(regions: IRegion[]) {
      this.regionsList0 = regions
        .filter((region) => ![3, 27, 425].includes(region.id))
        .sort((a, b) => a.orderPosition - b.orderPosition);
    },

    _setBankingRegions(regions: IBankRegion[]) {
      regions.sort((a, b) => a.orderPosition - b.orderPosition);
      this.bankingRegions0 = regions.map<IOfficesOfRegion>((region) => {
        region.offices.sort((a, b) => a.sortedItem - b.sortedItem);
        return { text: region.name, value: region.offices };
      });
    },

    setRoles(data: IRole[]) {
      this.roles0 = data;
    },

    async getSysParameters(): Promise<ISysParameter[]> {
      const mainStore = useMainStore();

      if (this.sysParameters.length) {
        return this.sysParameters;
      }

      try {
        const resp = await axios.get<ISysParameter[]>(mainStore.getUri.sysParameters);
        this.setSysParameters(resp.data);
        return resp.data;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getTakers(): Promise<void> {
      const mainStore = useMainStore();

      if (this.takers.length) {
        return Promise.resolve();
      }

      try {
        const resp = await axios.get<ITakers[]>(mainStore.getUri.usertakers);
        this.setTakers(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getStatusCount(): Promise<void> {
      const mainStore = useMainStore();

      try {
        const resp = await axios.get<IStatusBank[]>(mainStore.getUri.applicationStatuscount);
        this.setStatuses(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getSigners(): Promise<void> {
      const mainStore = useMainStore();

      if (this.signers.length) {
        return Promise.resolve();
      }

      try {
        const resp = await axios.get<ISigner[]>(mainStore.getUri.userSigner);
        this.setSigners(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getOffices(): Promise<IBankOffice[]> {
      const mainStore = useMainStore();

      if (this.officesList.length) {
        return this.officesList;
      }

      try {
        const resp = await axios.get<IBankOffice[]>(mainStore.getUri.office);
        this.setOffices(resp.data);
        return this.officesList;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getRegions(): Promise<IRegion[]> {
      const mainStore = useMainStore();

      if (this.regionsList.length) {
        return this.regionsList;
      }

      try {
        const resp = await axios.get<IRegion[]>(mainStore.getUri.region);
        this.setRegions(resp.data);
        return this.regionsList;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getBankingRegions(): Promise<IOfficesOfRegion[]> {
      const mainStore = useMainStore();

      if (this.isBankingRegions) {
        return this.bankingRegions;
      }

      try {
        const resp = await axios.get<IBank2[]>(mainStore.getUri.bankOffices);
        this._setBankingRegions(resp.data[0].regions);
        return this.bankingRegions;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getAllRoles(): Promise<IRole[]> {
      const mainStore = useMainStore();

      if (this.isRoles) {
        return this.roles;
      }

      const url = mainStore.getUri.roles;
      try {
        const resp = await axios.get<IRole[]>(url);
        this.setRoles(resp.data);
        return resp.data;
      } catch (e: any) {
        return this.roles;
      }
    },

    async getSystemSetting(): Promise<void> {
      const mainStore = useMainStore();

      if (this.sysSettings.length) {
        return Promise.resolve();
      }

      try {
        const resp = await axios.get<ISysSetting[]>(mainStore.getUri.mainsysset);
        this.setSysSettings(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },
  },
});

import { useMainStore } from './mainStore';
export default useBankStore;
